var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
/**
 * Create or rename a vocabulary — Nastavení → Slovníky (CZNA-1470).
 *
 * <p>Asks only what the methodologist knows: the name in Czech (required) and English,
 * and whether the vocabulary is open or closed. The code and the BIBFRAME role are the
 * system's — the server derives the code from the name and defaults the role to
 * `INTERNAL` — unlike the derivation-rule escape hatch (`CreateVocabularyTypeModal`),
 * which still asks for both.
 *
 * <p>A rename changes the names only. Open/closed is shown but fixed: flipping it would
 * change which existing terms are findings. The PUT is a full replace, so a rename echoes
 * every other field of the type back unchanged.
 */
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useForm, Controller } from 'react-hook-form';
import { Modal, ModalFooter, Button, TextField, Select, MessageBanner, } from '@folio/stripes/components';
import HelpTooltip from '../HelpTooltip';
import { useCreateVocabularyType, useUpdateVocabularyType } from '../../hooks/useVocabularyTypes';
import { getErrorInfo } from '../../utils/getErrorCode';
import { getErrorKind } from '../../utils/errorKind';
var FORM_ID = 'vocabulary-type-form';
var VALIDATION_TYPES = ['OPEN', 'CLOSED'];
var valuesOf = function (type) {
    var _a, _b, _c;
    return ({
        labelCs: (_a = type === null || type === void 0 ? void 0 : type.labelCs) !== null && _a !== void 0 ? _a : '',
        labelEn: (_b = type === null || type === void 0 ? void 0 : type.labelEn) !== null && _b !== void 0 ? _b : '',
        validationType: (_c = type === null || type === void 0 ? void 0 : type.validationType) !== null && _c !== void 0 ? _c : 'OPEN',
    });
};
var VocabularyTypeFormModal = function (_a) {
    var open = _a.open, type = _a.type, onClose = _a.onClose, onSaved = _a.onSaved;
    var intl = useIntl();
    var isRename = Boolean(type);
    var _b = useState(null), saveError = _b[0], setSaveError = _b[1];
    var _c = useForm({
        defaultValues: valuesOf(type),
        mode: 'onChange',
    }), control = _c.control, handleSubmit = _c.handleSubmit, reset = _c.reset, errors = _c.formState.errors;
    // Controlled-modal reset-on-open: a second open must not resurface the previous
    // attempt's input or error.
    useEffect(function () {
        if (open) {
            setSaveError(null);
            reset(valuesOf(type));
        }
    }, [open, type, reset]);
    var onError = function (err) { return __awaiter(void 0, void 0, void 0, function () {
        var info;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, getErrorInfo(err)];
                case 1:
                    info = _a.sent();
                    // CZNA-1625 — no answer in time: the write may have happened; never say "failed".
                    if (getErrorKind(info) === 'timeout') {
                        setSaveError(intl.formatMessage({ id: 'ui-czna-authorities.error.timeout' }));
                        return [2 /*return*/];
                    }
                    setSaveError(info.status === 409
                        ? intl.formatMessage({ id: 'ui-czna-authorities.vocabularyTypes.save.conflict' })
                        : info.message || intl.formatMessage({ id: 'ui-czna-authorities.vocabularyTypes.save.error' }));
                    return [2 /*return*/];
            }
        });
    }); };
    var createMutation = useCreateVocabularyType({
        onSuccess: function (saved) { return onSaved(saved, 'create'); },
        onError: onError,
    });
    var updateMutation = useUpdateVocabularyType({
        onSuccess: function (saved) { return onSaved(saved, 'rename'); },
        onError: onError,
    });
    var isSaving = createMutation.isLoading || updateMutation.isLoading;
    var onSubmit = function (values) {
        setSaveError(null);
        var labelCs = values.labelCs.trim();
        var labelEn = values.labelEn.trim() || undefined;
        if (type) {
            updateMutation.mutate({
                id: type.id,
                body: {
                    expectedUpdatedAt: type.updatedAt,
                    labelCs: labelCs,
                    labelEn: labelEn !== null && labelEn !== void 0 ? labelEn : null,
                    validationType: type.validationType,
                    bibframeRole: type.bibframeRole,
                    schemeUri: type.schemeUri,
                    externalSchemeUri: type.externalSchemeUri,
                },
            });
        }
        else {
            createMutation.mutate({ labelCs: labelCs, labelEn: labelEn, validationType: values.validationType });
        }
    };
    var validationTypeOptions = VALIDATION_TYPES.map(function (v) { return ({
        value: v,
        label: intl.formatMessage({ id: "ui-czna-authorities.vocabularyTypes.validationType.".concat(v) }),
    }); });
    var footer = (React.createElement(ModalFooter, null,
        React.createElement(Button, { buttonStyle: "primary", marginBottom0: true, type: "submit", form: FORM_ID, disabled: isSaving },
            React.createElement(FormattedMessage, { id: isRename
                    ? 'ui-czna-authorities.vocabularyTypes.form.save'
                    : 'ui-czna-authorities.vocabularyTypes.form.create' })),
        React.createElement(Button, { buttonStyle: "default", marginBottom0: true, onClick: onClose, disabled: isSaving },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.vocabularyTypes.form.cancel" }))));
    return (React.createElement(Modal, { open: open, onClose: onClose, label: intl.formatMessage({
            id: isRename
                ? 'ui-czna-authorities.vocabularyTypes.form.renameTitle'
                : 'ui-czna-authorities.vocabularyTypes.form.createTitle',
        }), dismissible: true, size: "small", footer: footer },
        saveError && (React.createElement(MessageBanner, { type: "error", dismissible: true, onExited: function () { return setSaveError(null); } }, saveError)),
        React.createElement("form", { id: FORM_ID, onSubmit: handleSubmit(onSubmit) },
            React.createElement(Controller, { name: "labelCs", control: control, rules: {
                    validate: function (v) { return v.trim() !== ''
                        || intl.formatMessage({ id: 'ui-czna-authorities.vocabularyTypes.form.labelCs.required' }); },
                }, render: function (_a) {
                    var _b;
                    var field = _a.field;
                    return (React.createElement(TextField, __assign({}, field, { label: intl.formatMessage({ id: 'ui-czna-authorities.vocabularyTypes.form.labelCs' }), error: (_b = errors.labelCs) === null || _b === void 0 ? void 0 : _b.message, required: true, autoFocus: true })));
                } }),
            React.createElement(Controller, { name: "labelEn", control: control, render: function (_a) {
                    var field = _a.field;
                    return (React.createElement(TextField, __assign({}, field, { label: intl.formatMessage({ id: 'ui-czna-authorities.vocabularyTypes.form.labelEn' }) })));
                } }),
            React.createElement(Controller, { name: "validationType", control: control, render: function (_a) {
                    var field = _a.field;
                    return (React.createElement("div", null,
                        React.createElement(Select, __assign({}, field, { label: intl.formatMessage({ id: 'ui-czna-authorities.vocabularyTypes.form.validationType' }), dataOptions: validationTypeOptions, disabled: isRename, onChange: function (e) { return field.onChange(e.target.value); } })),
                        React.createElement(HelpTooltip, { testKey: "vocabularyType-validationType", messageId: isRename
                                ? 'ui-czna-authorities.vocabularyTypes.form.validationType.fixed'
                                : 'ui-czna-authorities.vocabularyTypes.form.validationType.help' })));
                } }))));
};
export default VocabularyTypeFormModal;
